import React from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { AutomationFolder } from './AdminMailAutomationHub';
import { useNavigate } from 'react-router-dom';

interface AutomationSidebarProps {
  activeFolder: AutomationFolder;
  onFolderChange: (folder: AutomationFolder) => void;
}

const AutomationSidebar: React.FC<AutomationSidebarProps> = ({ 
  activeFolder, 
  onFolderChange 
}) => {
  const { isDark } = useAdminTheme();
  const navigate = useNavigate();

  const handleCompose = () => {
    // Navigate to the specialized professional platform composer
    navigate('/admin/platform-composer');
  };

  const folders: { id: AutomationFolder; label: string; icon: React.ReactNode }[] = [
    { 
      id: 'all', 
      label: 'All Platform Messages', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
        </svg>
      )
    },
    { 
      id: 'welcome', 
      label: 'Welcome Series', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    { 
      id: 'promotional', 
      label: 'Marketing Broadcasts', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
        </svg>
      )
    },
    { 
      id: 'system', 
      label: 'System Emails', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    { 
      id: 'sent', 
      label: 'Sent Messages', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
        </svg>
      )
    },
    { 
      id: 'drafts', 
      label: 'Drafts', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
    },
    { 
      id: 'archived', 
      label: 'Archived', 
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      )
    }
  ];

  return (
    <div className="flex flex-col h-full py-4">
      {/* Compose FAB style */}
      <div className="px-4 mb-6">
        <button 
          onClick={handleCompose}
          className={`group flex items-center gap-3 px-6 py-4 rounded-2xl transition-all shadow-md hover:shadow-xl active:scale-95 ${
            isDark 
              ? 'bg-slate-800 text-blue-400 hover:bg-slate-700 border border-slate-700' 
              : 'bg-white text-[#0B57D0] hover:bg-slate-50 border border-slate-100'
          }`}
        >
          <svg className="w-6 h-6 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          <span className="text-[14px] font-bold tracking-tight">Create email</span>
        </button>
      </div>

      <nav className="flex-1 space-y-0.5 px-3">
        {folders.map((folder) => (
          <button
            key={folder.id}
            onClick={() => onFolderChange(folder.id)}
            className={`w-full flex items-center justify-between px-4 py-2.5 rounded-full text-[14px] transition-all group ${
              activeFolder === folder.id
                ? (isDark ? 'bg-blue-600/20 text-blue-400 font-bold' : 'bg-[#E8F0FE] text-[#0B57D0] font-bold')
                : (isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-200' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')
            }`}
          >
            <div className="flex items-center gap-3">
              <span className={`transition-colors ${
                activeFolder === folder.id 
                  ? (isDark ? 'text-blue-400' : 'text-[#0B57D0]') 
                  : (isDark ? 'text-slate-500 group-hover:text-slate-200' : 'text-slate-400 group-hover:text-slate-900')
              }`}>
                {folder.icon}
              </span>
              <span>{folder.label}</span>
            </div>
          </button>
        ))}
      </nav>

      {/* Storage Indicator - Minimalist */}
      <div className="px-6 py-6 mt-auto">
        <div className="flex items-center justify-between mb-2">
          <span className={`text-[11px] font-bold uppercase tracking-wider ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>System Capacity</span>
          <span className={`text-[11px] font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>24%</span>
        </div>
        <div className={`h-1.5 w-full rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
          <div className="h-full bg-blue-500 w-[24%]" />
        </div>
      </div>
    </div>
  );
};

export default AutomationSidebar;
